import { useState, useEffect, useCallback } from 'react';
import { storage } from './utils';
import type { Language } from './types';
import { ar } from './ar';
import { en } from './en';

const translations = { ar, en };

// Constants
export const SUPPORTED_LANGUAGES: Language[] = ['ar', 'en'];
export const DEFAULT_LANGUAGE: Language = 'ar';
export const LANGUAGE_STORAGE_KEY = 'app_language';

// Language configuration
export const LANGUAGE_CONFIG = {
  ar: {
    name: 'العربية',
    nativeName: 'العربية',
    direction: 'rtl' as const,
    textAlign: 'text-right' as const,
    locale: 'ar-SA',
    flag: '🇸🇦'
  },
  en: {
    name: 'English',
    nativeName: 'English',
    direction: 'ltr' as const,
    textAlign: 'text-left' as const,
    locale: 'en-US',
    flag: '🇺🇸'
  }
} as const;

// Translation hook - محسن للأداء
export function useTranslation() {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
    return storage.get<Language>(LANGUAGE_STORAGE_KEY, DEFAULT_LANGUAGE);
  });

  const t = translations[currentLanguage];

  // Change language function - محسن للذاكرة
  const changeLanguage = useCallback((newLanguage: Language) => {
    if (!SUPPORTED_LANGUAGES.includes(newLanguage)) {
      console.warn(`Unsupported language: ${newLanguage}`);
      return;
    }

    setCurrentLanguage(newLanguage);
    storage.set(LANGUAGE_STORAGE_KEY, newLanguage);

    // Update document attributes - محسن للـ DOM
    if (typeof window !== 'undefined') {
      const config = LANGUAGE_CONFIG[newLanguage];
      document.documentElement.lang = newLanguage;
      document.documentElement.dir = config.direction;
    }
  }, []);

  // Toggle between Arabic and English - محسن للتفاعل
  const toggleLanguage = useCallback(() => {
    const newLang = currentLanguage === 'ar' ? 'en' : 'ar';
    changeLanguage(newLang);
  }, [currentLanguage, changeLanguage]);

  // Get current language configuration - محسن للذاكرة
  const getLanguageConfig = useCallback(() => {
    return LANGUAGE_CONFIG[currentLanguage];
  }, [currentLanguage]);

  // Initialize language on mount - محسن للتهيئة
  useEffect(() => {
    const savedLanguage = storage.get<Language>(LANGUAGE_STORAGE_KEY, DEFAULT_LANGUAGE);
    if (savedLanguage !== currentLanguage) {
      changeLanguage(savedLanguage);
    }
  }, [changeLanguage, currentLanguage]);

  return {
    t,
    currentLanguage,
    changeLanguage,
    toggleLanguage,
    getLanguageConfig,
    isRTL: currentLanguage === 'ar',
    isLTR: currentLanguage === 'en'
  };
}

// Translation utilities - محسن للأداء
export const i18nUtils = {
  // Get translation with fallback - محسن للبحث
  get: (key: string, lang: Language = 'ar', fallback?: string): string => {
    const keys = key.split('.');
    let value: unknown = translations[lang];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        // Fallback to Arabic if not found in current language
        if (lang !== 'ar') {
          return i18nUtils.get(key, 'ar', fallback);
        }
        return fallback || key;
      }
    }
    
    return typeof value === 'string' ? value : fallback || key;
  },

  // Format number based on language - محسن للتنسيق
  formatNumber: (num: number, lang: Language = 'ar'): string => {
    const config = LANGUAGE_CONFIG[lang];
    return new Intl.NumberFormat(config.locale).format(num);
  },

  // Format date based on language - محسن للتواريخ
  formatDate: (date: Date, lang: Language = 'ar', options?: Intl.DateTimeFormatOptions): string => {
    const config = LANGUAGE_CONFIG[lang];
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Intl.DateTimeFormat(config.locale, { ...defaultOptions, ...options }).format(date);
  },

  // Format currency based on language - محسن للعملة
  formatCurrency: (amount: number, currency: string = 'SAR', lang: Language = 'ar'): string => {
    const config = LANGUAGE_CONFIG[lang];
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency
    }).format(amount);
  },

  // Get plural form - محسن للجمع
  plural: (count: number, singular: string, plural: string, lang: Language = 'ar'): string => {
    if (lang === 'ar') {
      // Arabic plural rules
      if (count === 0) return `لا ${singular}`;
      if (count === 1) return `واحد ${singular}`;
      if (count === 2) return `اثنان ${singular}`;
      if (count >= 3 && count <= 10) return `${count} ${plural}`;
      return `${count} ${singular}`;
    } else {
      // English plural rules
      return count === 1 ? `1 ${singular}` : `${count} ${plural}`;
    }
  },

  // Interpolate variables in translation - محسن للاستبدال
  interpolate: (template: string, variables: Record<string, string | number>): string => {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return variables[key]?.toString() || match;
    });
  }
};

// Language detection utilities - محسن للكشف
export const languageDetection = {
  // Detect browser language - محسن للكشف
  detectBrowserLanguage: (): Language => {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
    
    const browserLang = navigator.language || navigator.languages?.[0] || '';
    const langCode = browserLang.split('-')[0].toLowerCase();
    
    if (langCode === 'ar') return 'ar';
    if (langCode === 'en') return 'en';
    
    return DEFAULT_LANGUAGE;
  },

  // Detect system language preference - محسن للتفضيلات
  detectSystemLanguage: (): Language => {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
    
    const systemLang = navigator.language || '';
    const langCode = systemLang.split('-')[0].toLowerCase();
    
    if (langCode === 'ar') return 'ar';
    if (langCode === 'en') return 'en';
    
    return DEFAULT_LANGUAGE;
  },

  // Get preferred language (stored > browser > system > default) - محسن للترتيب
  getPreferredLanguage: (): Language => {
    const stored = storage.get<Language | null>(LANGUAGE_STORAGE_KEY, null);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
      return stored;
    }
    
    const browserLang = languageDetection.detectBrowserLanguage();
    if (browserLang !== DEFAULT_LANGUAGE) {
      return browserLang;
    }
    
    const systemLang = languageDetection.detectSystemLanguage();
    if (systemLang !== DEFAULT_LANGUAGE) {
      return systemLang;
    }
    
    return DEFAULT_LANGUAGE;
  }
};

// Performance optimized translation cache - تخزين مؤقت محسن
const translationCache = new Map<string, string>();

export const cachedTranslation = {
  // Get cached translation
  get: (key: string, lang: Language): string | null => {
    const cacheKey = `${lang}:${key}`;
    return translationCache.get(cacheKey) || null;
  },

  // Set cached translation
  set: (key: string, lang: Language, value: string) => {
    const cacheKey = `${lang}:${key}`;
    translationCache.set(cacheKey, value);
  },

  // Clear cache
  clear: () => {
    translationCache.clear();
  },

  // Clear expired cache entries
  clearExpired: () => {
    // Simple cache cleanup - can be enhanced with timestamps
    if (translationCache.size > 1000) {
      translationCache.clear();
    }
  }
};

// Export types
export type TranslationKey = keyof typeof translations.ar;
export type TranslationFunction = (key: string, variables?: Record<string, string | number>) => string; 